import type { FC } from 'react';
import type { JobApplicationCardProps } from '../JobApplicationCard';
import { useTranslations } from '@vocab/react';
import {
  Inline,
  ButtonIcon,
  IconResume,
  IconDocument,
  IconNote,
} from 'braid-design-system';
import downloadFile from 'src/DocumentViewer/utils/downloadFile';
import { getNameByType, ATTACHMENT_TYPES } from 'src/constants/attachmentTypes';
import { TRACKING_CANDIDATE_LIST } from 'src/constants/pageTrackingData';
import { JOB_APPLICATION_TAB } from 'src/constants/tabs';
import { attachmentDownloadUrl } from 'src/utils/attachmentUrls';
import {
  trackDownloadDocumentEvent,
  trackDesktopClickedResumeInCandidateList,
  trackDesktopClickedCoverLetterInCandidateList,
  trackDesktopClickedSelectionCriteriaInCandidateList,
} from 'src/utils/events';
import translations from '../.vocab';

type Attachment = JobApplicationCardProps['attachments'][number];

export const AttachmentButtons: FC<
  Pick<
    JobApplicationCardProps,
    | 'attachments'
    | 'isMobile'
    | 'markApplicationAsRead'
    | 'jobApplication'
    | 'openDetailsDrawer'
    | 'isVerifiedId'
  >
> = ({
  isMobile,
  jobApplication,
  attachments,
  markApplicationAsRead,
  openDetailsDrawer,
  isVerifiedId,
}) => {
  const { t } = useTranslations(translations);

  const createAttachmentOnClick: () => (params: {
    attachment: Attachment;
    desktopHandler: () => void;
  }) => void =
    () =>
    ({ attachment, desktopHandler }) => {
      if (isMobile) {
        downloadFile(
          attachmentDownloadUrl(jobApplication.id, `${attachment.id}`),
          () => {
            markApplicationAsRead();
            trackDownloadDocumentEvent({
              candidateId: jobApplication.candidateId,
              documentType: getNameByType(attachment.type),
              attachmentId: attachment.id,
              currentPage: TRACKING_CANDIDATE_LIST,
              isVerifiedId,
            });
          },
        );
      } else {
        desktopHandler();
      }
    };

  const attachmentsMap = attachments.reduce<{
    resume: (Attachment & { onClick: () => void }) | null;
    coverLetter: (Attachment & { onClick: () => void }) | null;
    selectionCriteria: (Attachment & { onClick: () => void }) | null;
  }>(
    (prev, attachment) => {
      if (attachment.type === ATTACHMENT_TYPES.RESUME) {
        prev.resume = {
          ...attachment,
          onClick: () =>
            createAttachmentOnClick()({
              attachment,
              desktopHandler: () => {
                openDetailsDrawer(JOB_APPLICATION_TAB.RESUME);
                trackDesktopClickedResumeInCandidateList({
                  candidateId: jobApplication.candidateId,
                  currentPage: TRACKING_CANDIDATE_LIST,
                  isVerifiedId,
                });
              },
            }),
        };
      }

      if (attachment.type === ATTACHMENT_TYPES.COVER_LETTER) {
        prev.coverLetter = {
          ...attachment,
          onClick: () =>
            createAttachmentOnClick()({
              attachment,
              desktopHandler: () => {
                openDetailsDrawer(JOB_APPLICATION_TAB.COVER_LETTER);
                trackDesktopClickedCoverLetterInCandidateList({
                  candidateId: jobApplication.candidateId,
                  currentPage: TRACKING_CANDIDATE_LIST,
                  isVerifiedId,
                });
              },
            }),
        };
      }

      if (attachment.type === ATTACHMENT_TYPES.SELECTION_CRITERIA) {
        prev.selectionCriteria = {
          ...attachment,
          onClick: () =>
            createAttachmentOnClick()({
              attachment,
              desktopHandler: () => {
                openDetailsDrawer(JOB_APPLICATION_TAB.SELECTION_CRITERIA);
                trackDesktopClickedSelectionCriteriaInCandidateList({
                  candidateId: jobApplication.candidateId,
                  currentPage: TRACKING_CANDIDATE_LIST,
                  isVerifiedId,
                });
              },
            }),
        };
      }
      return prev;
    },
    {
      resume: null,
      coverLetter: null,
      selectionCriteria: null,
    },
  );

  const { resume, coverLetter, selectionCriteria } = attachmentsMap;

  return (
    <Inline space="medium">
      {resume && (
        <ButtonIcon
          variant="transparent"
          key={resume.id}
          id={`${resume.id}`}
          icon={<IconResume />}
          label={t('Resumé')}
          onClick={resume.onClick}
        />
      )}

      {coverLetter && (
        <ButtonIcon
          variant="transparent"
          key={coverLetter.id}
          id={`${coverLetter.id}`}
          icon={<IconDocument />}
          label={t('Cover Letter')}
          onClick={coverLetter.onClick}
        />
      )}

      {selectionCriteria && (
        <ButtonIcon
          variant="transparent"
          key={selectionCriteria.id}
          id={`${selectionCriteria.id}`}
          icon={<IconNote />}
          label={t('Selection Criteria')}
          onClick={selectionCriteria.onClick}
        />
      )}
    </Inline>
  );
};
